import { useState } from "react";
import { CookingPot, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const newRow = (r = { raw_material_id: "", quantity: "" }) =>
  ({ _key: crypto.randomUUID(), raw_material_id: r.raw_material_id, quantity: r.quantity });

export const RecipeDialog = ({ product, rawMaterials, onClose, onSaved }) => {
  const [rows, setRows] = useState(
    product.recipe.length ? product.recipe.map(newRow) : [newRow()]
  );

  const save = async () => {
    const items = rows.filter((r) => r.raw_material_id && parseFloat(r.quantity) > 0)
      .map((r) => ({ raw_material_id: r.raw_material_id, quantity: parseFloat(r.quantity) }));
    try {
      await api.put(`/inventory/recipe/${product.id}`, { items });
      toast.success(`Recipe saved (${items.length} raw material(s))`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setRow = (i, key, val) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const unitOf = (id) => rawMaterials.find((r) => r.id === id)?.unit || "";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="recipe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CookingPot size={18} weight="duotone" /> Recipe — {product.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">Raw materials consumed per 1 unit of this product. Deducted automatically on dispatch.</p>
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={row._key} className="flex items-center gap-2">
              <select value={row.raw_material_id} onChange={(e) => setRow(i, "raw_material_id", e.target.value)}
                data-testid={`recipe-raw-select-${i}`}
                className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2 py-2 text-sm outline-none">
                <option value="">Select raw material…</option>
                {rawMaterials.map((r) => <option key={r.id} value={r.id}>{`${r.name} (${r.unit})`}</option>)}
              </select>
              <input type="number" min="0" step="any" value={row.quantity} placeholder="Qty/unit"
                onChange={(e) => setRow(i, "quantity", e.target.value)} data-testid={`recipe-qty-input-${i}`}
                className="w-24 rounded-md border border-slate-300 px-2 py-2 text-sm outline-none" />
              <span className="w-10 text-xs text-slate-500">{unitOf(row.raw_material_id)}</span>
              <button onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}
                className="p-1 text-slate-400 hover:text-red-600" data-testid={`recipe-remove-row-${i}`}>
                <Trash size={15} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <button onClick={() => setRows((p) => [...p, newRow()])}
            data-testid="recipe-add-row-button"
            className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
            <Plus size={13} weight="bold" /> Add raw material
          </button>
          <button onClick={save} data-testid="recipe-save-button"
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700">
            Save Recipe
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
